/**
 * Crawler Stats Tab
 * Job, place, proxy, and data quality analytics
 */

import { useMemo } from 'react';
import { Row, Col, Card, Table, Tag, Statistic, Empty, Alert, Skeleton, Progress } from 'antd';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useCrawlerStats } from '@/hooks/useProjectStats';
import { usePreferencesStore } from '@/stores/usePreferencesStore';
import { CHART_COLORS } from '@/styles/theme';
import { formatNumber } from '@/utils/formatters';

const PIE_COLORS = [CHART_COLORS.success, CHART_COLORS.primary, CHART_COLORS.warning, CHART_COLORS.error, CHART_COLORS.purple, CHART_COLORS.cyan];

const STATUS_COLORS: Record<string, string> = {
  completed: CHART_COLORS.success,
  running: CHART_COLORS.primary,
  pending: CHART_COLORS.warning,
  failed: CHART_COLORS.error,
  cancelled: CHART_COLORS.purple,
};

export function CrawlerTab() {
  const { data, isLoading, isError, error } = useCrawlerStats();
  const isDark = usePreferencesStore((s) => s.theme) === 'dark';

  // Pivot jobsOverTime for stacked bar chart
  const { jobsOverTimeData, jobStatuses } = useMemo(() => {
    if (!data?.jobsOverTime) return { jobsOverTimeData: [], jobStatuses: [] };
    const map = new Map<string, Record<string, any>>();
    const statusSet = new Set<string>();
    for (const row of data.jobsOverTime) {
      const entry = map.get(row.month) || { month: row.month };
      entry[row.status] = row.count;
      map.set(row.month, entry);
      statusSet.add(row.status);
    }
    return {
      jobsOverTimeData: [...map.values()].sort((a, b) => a.month.localeCompare(b.month)),
      jobStatuses: [...statusSet],
    };
  }, [data?.jobsOverTime]);

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {[1, 2, 3].map((i) => <Card key={i} size="small"><Skeleton active paragraph={{ rows: 4 }} /></Card>)}
    </div>
  );
  if (isError) return <Alert type="error" message="Failed to load crawler stats" description={(error as Error)?.message} showIcon />;
  if (!data) return <Empty description="No crawler stats available" />;

  const gridColor = isDark ? '#303030' : '#f0f0f0';
  const textColor = isDark ? '#8c8c8c' : '#595959';
  const bgColor = isDark ? '#1f1f1f' : '#fff';
  const tooltipStyle = { backgroundColor: bgColor, border: `1px solid ${gridColor}`, borderRadius: 8 };

  // Data completeness as percentages
  const dc = data.dataCompleteness;
  const completeness = dc && dc.total > 0 ? [
    { field: 'Address', pct: Math.round((dc.has_address / dc.total) * 100) },
    { field: 'Rating', pct: Math.round((dc.has_rating / dc.total) * 100) },
    { field: 'Phone', pct: Math.round((dc.has_phone / dc.total) * 100) },
    { field: 'Website', pct: Math.round((dc.has_website / dc.total) * 100) },
    { field: 'Hours', pct: Math.round((dc.has_hours / dc.total) * 100) },
    { field: 'Images', pct: Math.round((dc.has_images / dc.total) * 100) },
  ] : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Row gutter={16}>
        {/* Jobs by Status */}
        <Col xs={24} lg={8}>
          <Card title="Jobs by Status" size="small">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={data.jobsByStatus}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ status, count }) => `${status} (${count})`}
                  labelLine={false}
                >
                  {data.jobsByStatus.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatNumber(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Data Completeness */}
        <Col xs={24} lg={8}>
          <Card title="Data Completeness" size="small">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }}>
              {completeness.map((item) => (
                <div key={item.field} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 60, fontSize: 13 }}>{item.field}</span>
                  <Progress percent={item.pct} size="small" style={{ flex: 1, margin: 0 }} />
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', marginTop: 8, color: textColor, fontSize: 12 }}>
              Based on {formatNumber(dc?.total || 0)} total places
            </div>
          </Card>
        </Col>

        {/* Owner Verified */}
        <Col xs={24} lg={8}>
          <Card title="Owner Verification" size="small">
            {data.ownerVerified && (
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic title="Verified" value={data.ownerVerified.verified} formatter={(v) => formatNumber(v as number)} valueStyle={{ color: CHART_COLORS.success }} />
                </Col>
                <Col span={12}>
                  <Statistic title="Unverified" value={data.ownerVerified.unverified} formatter={(v) => formatNumber(v as number)} valueStyle={{ color: CHART_COLORS.warning }} />
                </Col>
                <Col span={24} style={{ marginTop: 16 }}>
                  <Progress
                    percent={data.ownerVerified.total > 0 ? Math.round((data.ownerVerified.verified / data.ownerVerified.total) * 100) : 0}
                    format={(pct) => `${pct}% verified`}
                    status="active"
                  />
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      </Row>

      {/* Jobs Over Time - Stacked Bar */}
      {jobsOverTimeData.length > 0 && (
        <Card title="Jobs Over Time" size="small">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={jobsOverTimeData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="month" tick={{ fill: textColor, fontSize: 11 }} />
              <YAxis tick={{ fill: textColor, fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
              {jobStatuses.map((status, i) => (
                <Bar
                  key={status}
                  dataKey={status}
                  stackId="status"
                  fill={STATUS_COLORS[status] || PIE_COLORS[i % PIE_COLORS.length]}
                  radius={i === jobStatuses.length - 1 ? [4, 4, 0, 0] : undefined}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Cumulative Places */}
      <Card title="Cumulative Places Over Time" size="small">
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.cumulativePlaces} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            <XAxis dataKey="week" tick={{ fill: textColor, fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
            <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatNumber(v)} />
            <Legend />
            <Line type="monotone" dataKey="cumulative" name="Cumulative" stroke={CHART_COLORS.primary} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="count" name="Weekly" stroke={CHART_COLORS.success} strokeWidth={1} dot={false} opacity={0.6} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Cell Completion + Failure Reasons */}
      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card title="Cell Completion Rates" size="small">
            <Table
              dataSource={data.cellCompletionRates}
              rowKey="job_id"
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ y: 400 }}
              columns={[
                { title: 'Job', dataIndex: 'job_name', key: 'job_name', ellipsis: true, width: 140 },
                { title: 'Query', dataIndex: 'search_query', key: 'search_query', ellipsis: true, width: 120 },
                { title: 'Status', dataIndex: 'status', key: 'status', width: 90, render: (v: string) => <Tag color={STATUS_COLORS[v] || undefined}>{v}</Tag> },
                { title: 'Cells', dataIndex: 'total_cells', key: 'total_cells', width: 70, align: 'right' as const, render: (v: number) => formatNumber(v) },
                {
                  title: 'Completion', dataIndex: 'completion_pct', key: 'completion_pct', width: 150,
                  sorter: (a: any, b: any) => a.completion_pct - b.completion_pct,
                  render: (v: number) => (
                    <Progress
                      percent={v}
                      size="small"
                      strokeColor={v >= 80 ? CHART_COLORS.success : v >= 50 ? CHART_COLORS.warning : CHART_COLORS.error}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Cell Failure Reasons" size="small">
            <Table
              dataSource={data.cellFailureReasons}
              rowKey="error"
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ y: 400 }}
              columns={[
                { title: 'Error', dataIndex: 'error', key: 'error', ellipsis: true },
                { title: 'Count', dataIndex: 'count', key: 'count', width: 90, align: 'right' as const, sorter: (a: any, b: any) => a.count - b.count, render: (v: number) => formatNumber(v) },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Rating Distribution */}
        <Col xs={24} lg={8}>
          <Card title="Rating Distribution" size="small">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.ratingDistribution} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="bucket" tick={{ fill: textColor, fontSize: 11 }} />
                <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), 'Places']} />
                <Bar dataKey="count" fill={CHART_COLORS.warning} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Review Count Distribution */}
        <Col xs={24} lg={8}>
          <Card title="Review Count Distribution" size="small">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data.reviewCountDist} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="bucket" tick={{ fill: textColor, fontSize: 11 }} />
                <YAxis tick={{ fill: textColor, fontSize: 11 }} tickFormatter={formatNumber} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [formatNumber(v), 'Places']} />
                <Bar dataKey="count" fill={CHART_COLORS.purple} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Price Level Distribution */}
        <Col xs={24} lg={8}>
          <Card title="Price Level Distribution" size="small">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={data.priceLevelDist}
                  dataKey="count"
                  nameKey="level"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  label={({ level, count }) => `${level} (${count})`}
                  labelLine={false}
                >
                  {data.priceLevelDist.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => formatNumber(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Top Categories */}
        <Col xs={24} lg={12}>
          <Card title="Top Categories" size="small">
            <Table
              dataSource={data.topCategories.slice(0, 20)}
              rowKey="category"
              size="small"
              pagination={false}
              scroll={{ y: 400 }}
              columns={[
                { title: 'Category', dataIndex: 'category', key: 'category', ellipsis: true },
                { title: 'Places', dataIndex: 'count', key: 'count', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v) },
              ]}
            />
          </Card>
        </Col>

        {/* Places by Country */}
        <Col xs={24} lg={12}>
          <Card title="Places by Country" size="small">
            <Table
              dataSource={data.placesByCountry.slice(0, 20)}
              rowKey="country"
              size="small"
              pagination={false}
              scroll={{ y: 400 }}
              columns={[
                { title: 'Country', dataIndex: 'country', key: 'country' },
                { title: 'Places', dataIndex: 'count', key: 'count', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v) },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Job Durations Histogram */}
      {data.jobDurations.length > 0 && (
        <Card title="Job Duration Distribution" size="small">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={data.jobDurations} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
              <XAxis dataKey="label" tick={{ fill: textColor, fontSize: 11 }} />
              <YAxis tick={{ fill: textColor, fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [v, 'Jobs']} />
              <Bar dataKey="count" fill={CHART_COLORS.cyan} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Proxy Success Rates */}
      {data.proxySuccessRates.length > 0 && (
        <Card title="Proxy Success Rates" size="small">
          <Table
            dataSource={data.proxySuccessRates}
            rowKey="proxy_id"
            size="small"
            pagination={false}
            scroll={{ y: 300 }}
            columns={[
              { title: 'Host', dataIndex: 'host', key: 'host', ellipsis: true },
              { title: 'Score', dataIndex: 'score', key: 'score', width: 80, align: 'right' as const },
              { title: 'Total', dataIndex: 'total_requests', key: 'total_requests', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v) },
              { title: 'Success', dataIndex: 'success_rate', key: 'success_rate', width: 100, align: 'right' as const, render: (v: number) => <span style={{ color: v >= 80 ? CHART_COLORS.success : v >= 50 ? CHART_COLORS.warning : CHART_COLORS.error }}>{v}%</span> },
            ]}
          />
        </Card>
      )}

      {/* Top Search Queries */}
      <Card title="Top Search Queries by Place Yield" size="small">
        <Table
          dataSource={data.topSearchQueries.slice(0, 30)}
          rowKey="search_query"
          size="small"
          pagination={false}
          scroll={{ y: 400 }}
          columns={[
            { title: 'Search Query', dataIndex: 'search_query', key: 'search_query', ellipsis: true },
            { title: 'Unique Places', dataIndex: 'unique_places', key: 'unique_places', width: 130, align: 'right' as const, render: (v: number) => formatNumber(v) },
            { title: 'Jobs', dataIndex: 'job_count', key: 'job_count', width: 80, align: 'right' as const },
          ]}
        />
      </Card>

      {/* Places Per Job */}
      <Card title="Places per Search Query" size="small">
        <Table
          dataSource={data.placesPerJob}
          rowKey="search_query"
          size="small"
          pagination={{ pageSize: 15 }}
          columns={[
            { title: 'Search Query', dataIndex: 'search_query', key: 'search_query', ellipsis: true },
            { title: 'Jobs', dataIndex: 'job_count', key: 'job_count', width: 80, align: 'right' as const },
            { title: 'Unique Places', dataIndex: 'unique_places', key: 'unique_places', width: 130, align: 'right' as const, sorter: (a: any, b: any) => a.unique_places - b.unique_places, render: (v: number) => formatNumber(v) },
          ]}
        />
      </Card>

      {/* Duplicate Rates */}
      {data.duplicateRates.length > 0 && (
        <Card title="Duplicate Rates per Job" size="small">
          <Table
            dataSource={data.duplicateRates}
            rowKey="job_id"
            size="small"
            pagination={{ pageSize: 15 }}
            columns={[
              { title: 'Job', dataIndex: 'name', key: 'name', ellipsis: true, width: 140 },
              { title: 'Search Query', dataIndex: 'search_query', key: 'search_query', ellipsis: true },
              { title: 'Total', dataIndex: 'total_places', key: 'total_places', width: 90, align: 'right' as const, render: (v: number) => formatNumber(v) },
              { title: 'Duplicates', dataIndex: 'duplicate_places', key: 'duplicate_places', width: 100, align: 'right' as const, render: (v: number) => formatNumber(v) },
              {
                title: 'Dup %', dataIndex: 'duplicate_pct', key: 'duplicate_pct', width: 80, align: 'right' as const,
                sorter: (a: any, b: any) => a.duplicate_pct - b.duplicate_pct,
                render: (v: number) => <span style={{ color: v >= 30 ? CHART_COLORS.error : v >= 10 ? CHART_COLORS.warning : CHART_COLORS.success }}>{v}%</span>,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
